'use client';

import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FilterItem } from '@/components/organisms/filter-card-list/filter-card-list';
import {
  DateData,
  LocationChangeParameters,
  LocationData,
  LocationGroup,
  PersonsData,
  Room,
  RoomsState,
  SearchBarDefaultValues,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import variables from '@/components/organisms/search-bar/search-bar.variables';
import {
  extractAdultCount,
  extractAdultsPerRoom,
  extractAutomaticRoomAssignment,
  extractCheckedLocations,
  extractChildrenAges,
  extractChildrenAgesPerRoom,
  extractDepartureDate,
  extractDepartureDateAsISO,
  extractDestinationRegionIds,
  extractDurationRange,
  extractDurationSecondary,
  extractHotelIds,
  extractTransportationMethod,
  generateDateSectionValueString,
  generatePersonsSectionValueString,
  isPersonsSectionValid,
  mergeObjects,
} from '@/components/organisms/search-bar/shared/search-utils/search-utils';
import { AdaptDestinationHierarchy } from '@/integrations/content/content-destinations/destinations-hierarchy/destination-hierarchy.adapter';
import useQueryDestinationHierarchy from '@/integrations/content/content-destinations/destinations-hierarchy/destination-hierarchy.hooks';
import useQuerySearchSettings from '@/integrations/content/search-settings/search-settings.hooks';
import { useQueryHotelsWithOffers } from '@/integrations/offers/hotels-with-offers/hotels-with-offers.hooks';
import { AdaptSearchAvailableDates } from '@/integrations/offers/search/available-dates/available-dates.adapter';
import useQuerySearchAvailableDates from '@/integrations/offers/search/available-dates/available-dates.hooks';
import { trackSubmitSearchEvent } from '@/integrations/tracking/google-tag-manager/scripts';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useRecentSearchesContext } from '@/libraries/contexts/recent-searches-context/recent-searches-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import {
  searchPageEvents$,
  SearchPageEventType,
} from '@/libraries/events/page-events';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';
import { getRangeEndDate } from '@/libraries/getters/get-range-end-date';
import { getSearchParameters } from '@/libraries/getters/get-search-parameters';
import useUiEventOutsideHandler from '@/libraries/hooks/use-ui-event-outside';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import validateSameDate from '@/libraries/validators/validate-same-date';
import SearchBarWrapper from './search-bar-wrapper/search-bar-wrapper';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './search-bar.module.scss';

export interface SearchBarProps {
  /** Indicates whether to automatically trigger search when no URL parameters are present */
  autoSearch?: boolean;
  /** Indicates whether the search bar is only buttons to open the widget modal  */
  buttonMode?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether to show the compact version */
  compact?: boolean;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** Default values for the search bar */
  propDefaultValues?: SearchBarDefaultValues;
  /** The search settings */
  searchSettings: Content.Components['SearchSettingsDto'];
  /** Indicates weather the search should trigger a navigation to the search page (used when the search bar is used on a different page)*/
  shouldNavigate?: boolean;
  /** Indicates weather the search should trigger a refresh of the page with the updated url params */
  shouldRefresh?: boolean;
  /** The travel portal search settings */
  travelPortalSearchSettings?:
    | Content.Components['TravelPortalSearchSettingsDto']
    | undefined;
}

export const SearchBar = forwardRef(
  (
    {
      autoSearch = false,
      buttonMode = false,
      className,
      compact = false,
      isWidget = false,
      propDefaultValues,
      searchSettings,
      shouldNavigate = false,
      shouldRefresh = false,
    }: SearchBarProps,
    ref,
  ) => {
    const { t } = useI18n();
    const router = useRouter();
    const { dateFormatter } = useDateFormatter();
    const rootRef = useRef<HTMLDivElement>(null);
    const searchParams = useSearchParams();
    const [shouldFetchBrandSearchSettings, setShouldFetchBrandSearchSettings] =
      useState(false);
    const [brandSearchSettings, setBrandSearchSettings] =
      useState(searchSettings);
    const [recentSearchLocationText, setRecentSearchLocationText] = useState<
      string[] | string
    >();
    const { addRecentSearch } = useRecentSearchesContext();
    const {
      activeSection,
      brand,
      periodType,
      setActiveSection,
      setAdultsNo,
      setAdultsNoPerRoom,
      setAutomaticRoomAssignment,
      setBrand,
      setChildrenAges,
      setChildrenAgesPerRoom,
      setDepartureDate,
      setDepartureIds,
      setDestinationIds,
      setDestinationRegionIds,
      setHotelIds,
      setIsClickedEditSearch,
      setPeriodType,
      setTriggeredBy,
      updateCookies,
      ...searchContext
    } = useSearchContext();
    const { configurationFlowAnchorLinkId, hotelDetails } =
      usePageDetailsContext();
    const initialBrand = useRef(brand);
    const isLaptop = useWindowSize('laptop');
    const showBrandSelectorLedge = variables.showBrandSelectorLedge;
    const showBrandSelectorSection = variables.showBrandSelectorSection;

    const hasDefaultValue = (parameterName: string): boolean => {
      // Check if parameter is in searchParams
      const isInQuery = searchParams!.has(parameterName);

      // Check if parameter is in searchDefaults
      const isInDefaults = propDefaultValues
        ? Object.hasOwnProperty.call(propDefaultValues, parameterName)
        : false;

      return isInQuery || isInDefaults;
    };

    const [locationSection, setLocationSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          locations: [],
          transportationOptions: [],
        },
        id: 1,
        isLoading:
          hasDefaultValue('destinationIds') || hasDefaultValue('departureIds'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.location.locationTitle'),
        placeholder: t('items.search.searchBar.location.locationPlaceholder'),
        type: SearchBarWrapperSectionType.LOCATIONS,
      });

    const [personsSection, setPersonsSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          adultThreshold: searchSettings.adultAgeThreshold,
          defaultAdultNumber: searchSettings.defaultNumberOfAdultsPerRoom,
          maxPersons: searchSettings.maxNumberOfAdults,
          maxRooms: searchSettings.maxNumberOfRooms,
          roomDistributionWarning: searchSettings.roomDistributionWarning,
          roomsState: {
            automaticRoomAssignment: searchSettings.automaticRoomAssignment,
            rooms: [
              {
                childrenAges: [],
                numberOfAdults: searchSettings.defaultNumberOfAdultsPerRoom,
                numberOfChildren: 0,
              },
            ],
          },
        },
        id: 2,
        isLoading:
          hasDefaultValue('adultsNumber') ||
          hasDefaultValue('childrenAges') ||
          hasDefaultValue('adultsPerRoom') ||
          hasDefaultValue('childrenAgesPerRoom'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.persons.personsTitle'),
        placeholder: t('items.search.searchBar.persons.personsPlaceholder'),
        type: SearchBarWrapperSectionType.PERSONS,
        value: generatePersonsSectionValueString({
          adults: searchSettings?.selectedPaxDistribution?.adultsNo,
          children: searchSettings?.selectedPaxDistribution?.childrenNo,
          t,
        }),
      });

    const [brandSection, setBrandSection] =
      useState<SearchBarWrapperSectionData>({
        id: 0,
        label: brand,
        type: SearchBarWrapperSectionType.BRAND,
      });

    const [dateSection, setDateSection] = useState<SearchBarWrapperSectionData>(
      {
        dropdownData: {
          availableDates: [],
          durationOptions: [],
        },
        id: 3,
        isLoading: hasDefaultValue('departureDate'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.date.dateTitle'),
        placeholder: t('items.search.searchBar.date.datePlaceholder'),
        type: SearchBarWrapperSectionType.DATE,
      },
    );

    const generateLocationValue = useCallback(
      ({
        locations,
        transportationOptions,
      }: {
        locations?: LocationGroup[];
        transportationOptions?: FilterItem[];
      }): string[] => {
        const activeTransports =
          transportationOptions
            ?.filter(option => option.isSelected && option.headline)
            .map(option => option.headline) || [];

        const activeLocations =
          locations
            ?.flatMap(group =>
              group.options.filter(
                option => option.checked && !option.disabled && option.label,
              ),
            )
            .map(option => option.label) || [];

        const hasTransports = activeTransports.length > 0;
        const hasLocations = activeLocations.length > 0;

        if (hasTransports) {
          const transportsString = activeTransports.join(', ');

          if (hasLocations) {
            setRecentSearchLocationText([...(activeLocations as string[])]);

            return [
              `${transportsString} ${t('items.common.words.to')}`,
              ...(activeLocations as string[]),
            ];
          }

          setRecentSearchLocationText(
            `${t('items.search.searchBar.location.toAllDestinations')}`,
          );

          return [
            `${transportsString} ${t('items.search.searchBar.location.toAllDestinations')}`,
          ];
        }

        return activeLocations as string[];
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [locationSection],
    );

    const getDuration = useCallback(() => {
      return extractDurationSecondary(dateSection);
    }, [dateSection]);

    const getDurationRange = useCallback(() => {
      return extractDurationRange(dateSection);
    }, [dateSection]);

    const getTransportationMethod = useCallback(() => {
      return extractTransportationMethod(
        locationSection,
        isWidget,
        searchContext.departureIds,
      );
    }, [locationSection, isWidget, searchContext.departureIds]);

    const getCheckedLocations = useCallback(() => {
      return extractCheckedLocations(locationSection);
    }, [locationSection]);

    const getHotelIds = useCallback(() => {
      return extractHotelIds({
        hotelDetails,
        isWidget,
        locationSection,
      });
    }, [hotelDetails, isWidget, locationSection]);

    const getDestinationRegionIds = useCallback(() => {
      return extractDestinationRegionIds(locationSection);
    }, [locationSection]);

    const getAdultCount = useCallback(() => {
      return extractAdultCount(personsSection);
    }, [personsSection]);

    const getChildrenAges = useCallback(() => {
      return extractChildrenAges(personsSection);
    }, [personsSection]);

    const getAdultsPerRoom = useCallback(() => {
      return extractAdultsPerRoom(personsSection);
    }, [personsSection]);

    const getChildrenAgesPerRoom = useCallback(() => {
      return extractChildrenAgesPerRoom(personsSection);
    }, [personsSection]);

    const getAutomaticRoomAssignment = useCallback(() => {
      return extractAutomaticRoomAssignment(personsSection);
    }, [personsSection]);

    const getDepartureDate = useCallback(() => {
      return extractDepartureDate(dateSection);
    }, [dateSection]);

    const selectedDurationType = getDuration();

    const isEnabledSearchButton = useMemo(
      () =>
        getDuration() !== undefined &&
        getTransportationMethod() !== undefined &&
        getDepartureDate() !== undefined,
      [getDuration, getTransportationMethod, getDepartureDate],
    );

    const destinationIdsParam = isWidget ? '' : getCheckedLocations();

    const { data: dataSearchSettings } = useQuerySearchSettings({
      brand: brand,
      enabled: shouldFetchBrandSearchSettings,
    });

    const {
      data: dataDestinationHierarchy,
      isFetching: isFetchingDestinationHierarchy,
    } = useQueryDestinationHierarchy({
      adultsNumber:
        brandSearchSettings.selectedPaxDistribution?.adultsNo.toString() as string,
      brand: brand,
      durationType: 'all',
      hotelIds: isWidget ? (getHotelIds() as string) : '',
    });

    const { data: dataAvailableDates, isFetching: isFetchingAvailableDates } =
      useQuerySearchAvailableDates({
        adultsNumber: getAdultCount()?.toString() as string,
        brand: brand,
        childrenAges: getChildrenAges() as string,
        departureIds: getTransportationMethod() as string,
        destinationIds: destinationIdsParam,
        destinationRegionIds: getDestinationRegionIds(),
        hotelIds: getHotelIds() as string,
      });

    const { data: dataSearchSingle } = useQueryHotelsWithOffers({
      batchSize: '1',
      brand: brand,
      departureDate: getDepartureDate() as string,
      departureIds: getTransportationMethod() as string,
      durationRange: getDurationRange()?.toString() as string,
      durationType: getDuration() as Content.Components['PeriodType'],
      hotelIds: getHotelIds() as string,
      ...(getAutomaticRoomAssignment()
        ? {
            adultsNumber: getAdultCount()?.toString() as string,
            childrenAges: getChildrenAges() as string,
          }
        : {
            adultsPerRoom: getAdultsPerRoom(),
            childrenAgesPerRoom: getChildrenAgesPerRoom(),
          }),
    });

    const getDurationOptionsFromSearchSettings = (
      selectedDurationType?: string,
    ) => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const activeDurationType =
        selectedDurationType ||
        (!isWidget ? periodType : undefined) ||
        defaultValues.durationType ||
        brandSearchSettings.selectedPeriodType;

      return brandSearchSettings.availablePeriodTypes.map(option => {
        let rangeSize: number;

        // TODO: Handle rangeSize issue (#2311) gracefully
        switch (option.value) {
          case 'week':
            rangeSize = 7;
            break;

          case 'twoWeeks':
            rangeSize = 14;
            break;

          case 'threeWeeks':
            rangeSize = 21;
            break;

          default: {
            rangeSize = 0;
          }
        }

        return {
          headline: option.label,
          icon: 'Calendar' as IconName,
          id: option.value,
          isSelected: option.value === activeDurationType,
          rangeSize,
          text: option.description,
        };
      });
    };

    const setupAndUpdateDurationOptions = () => {
      const newDurationOptions = getDurationOptionsFromSearchSettings();

      // Fallback
      const anySelected = newDurationOptions.some(option => option.isSelected);

      if (!anySelected && newDurationOptions.length > 0) {
        newDurationOptions[0].isSelected = true;
      }

      setDateSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as DateData),
            durationOptions: newDurationOptions,
            rangeSize: newDurationOptions.find(option => option.isSelected)
              ?.rangeSize,
          },
        };
      });
    };

    useEffect(() => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const updateDestinationsAndDeparturesInSections = (
        data: AdaptDestinationHierarchy,
      ) => {
        setLocationSection(previousSection => {
          // Departures
          const selectedDepartureIdFromSettings =
            brandSearchSettings.overrideDeparture
              ? defaultValues?.departureId
              : !isWidget && searchContext.departureIds
                ? searchContext.departureIds
                : defaultValues?.departureId;

          const selectedDepartureId =
            selectedDepartureIdFromSettings ?? data.departures[0]?.id;

          const updatedTransportationOptions = data.departures.map(
            departure => ({
              ...departure,
              isSelected: departure.id === selectedDepartureId,
            }),
          );

          // Destinations
          const selectedLocations = [
            ...(defaultValues?.destinationIds ?? []),
            ...(defaultValues?.destinationRegionIds ?? []),
          ];

          // filter locations based on transportation method
          const updatedLocations = data.destinations?.map(group => ({
            ...group,
            options: group.options.map(location => ({
              ...location,
              checked: selectedLocations.includes(location.id),
              disabled: !location.filterIds?.includes(
                parseInt(selectedDepartureId),
              ),
            })),
          }));

          return {
            ...previousSection,
            dropdownData: {
              ...previousSection.dropdownData,
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            } as LocationData,
            isLoading: false,
            value: generateLocationValue({
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            }),
          };
        });
      };

      if (
        !dataDestinationHierarchy?.departures ||
        !dataDestinationHierarchy?.destinations
      )
        return;

      updateDestinationsAndDeparturesInSections({
        departures: dataDestinationHierarchy?.departures,
        destinations: dataDestinationHierarchy?.destinations,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataDestinationHierarchy]);

    const getDepartureDateParameter = useCallback((): Date | undefined => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const departureDateParameter = defaultValues.departureDate
        ? new Date(defaultValues.departureDate)
        : undefined;

      return departureDateParameter;
    }, [searchParams, propDefaultValues]);

    const getFilteredAvailableDates = (
      data: AdaptSearchAvailableDates,
      durationId: Content.Components['PeriodType'],
    ) => {
      return (
        data.result
          ?.filter(item => item.availablePeriodTypes?.includes(durationId))
          .map(item => new Date(item.departureDate)) ?? []
      );
    };

    useEffect(() => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const departureDateParameter = defaultValues.departureDate
        ? new Date(defaultValues.departureDate)
        : undefined;

      const updateDatesInSections = (data: AdaptSearchAvailableDates) => {
        setDateSection(previousSection => {
          const currentDuration =
            (previousSection.dropdownData as DateData).durationOptions?.find(
              option => option.isSelected,
            )?.id ?? getDuration();
          const availablePeriodTypes = new Set<string>(
            data.result?.flatMap(item => item.availablePeriodTypes ?? []) ?? [],
          );
          const durationOptionsFromSearchSettings =
            getDurationOptionsFromSearchSettings(currentDuration as string);
          const filteredDurationOptions =
            durationOptionsFromSearchSettings.filter(
              option => !!option.id && availablePeriodTypes.has(option.id),
            );
          const selectedDurationId =
            filteredDurationOptions.find(option => option.isSelected)?.id ||
            filteredDurationOptions[0]?.id;
          const resolvedDuration = selectedDurationId || currentDuration;
          const durationOptions = filteredDurationOptions.map(option => ({
            ...option,
            isSelected: option.id === selectedDurationId,
          }));
          const availableDates = getFilteredAvailableDates(
            data,
            resolvedDuration as Content.Components['PeriodType'],
          );
          const activeDuration = (resolvedDuration ?? getDuration()) as
            | string
            | undefined;

          const contextDate =
            !isWidget && searchContext.departureDate
              ? new Date(searchContext.departureDate)
              : undefined;
          const savedDate =
            contextDate ??
            (previousSection.dropdownData as DateData)?.selectedDate;
          const today = new Date();

          today.setUTCHours(0, 0, 0, 0);

          // Check if saved date from cookie is still valid
          const isSavedDateValid =
            savedDate &&
            savedDate >= today &&
            availableDates.some(date => validateSameDate(date, savedDate));

          const isUrlDateAvailable =
            departureDateParameter &&
            availableDates.some(date =>
              validateSameDate(date, departureDateParameter),
            );

          // Use saved date if valid, otherwise use URL param if available, otherwise first available date
          const selectedDate = isSavedDateValid
            ? savedDate
            : isUrlDateAvailable
              ? departureDateParameter
              : availableDates[0];
          const formattedStartDate = selectedDate
            ? dateFormatter(selectedDate).format('D. MMM YYYY')
            : '';

          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as DateData),
              availableDates: activeDuration === 'custom' ? [] : availableDates,
              durationOptions,
              maxDate:
                activeDuration === 'custom'
                  ? new Date(brandSearchSettings.maxDepartureDate as string)
                  : undefined,
              minDate:
                activeDuration === 'custom'
                  ? new Date(brandSearchSettings.minDepartureDate as string)
                  : undefined,
              rangeSelect: activeDuration === 'custom',
              rangeSize:
                activeDuration === 'custom'
                  ? undefined
                  : durationOptions.find(option => option.isSelected)
                      ?.rangeSize,
              selectedDate,
            },
            isLoading: false,
            isNotified: !isSavedDateValid && !isUrlDateAvailable,
            value: generateDateSectionValueString({
              duration: activeDuration,
              startDate: formattedStartDate,
              t,
            }),
          };
        });
      };

      if (!dataAvailableDates) return;

      updateDatesInSections(dataAvailableDates);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAvailableDates, selectedDurationType]);

    const invalidatePersonsSection = () => {
      setPersonsSection(previousSection => {
        if (isPersonsSectionValid(previousSection)) {
          return {
            ...previousSection,
            isNotified: false,
          };
        }

        return {
          ...previousSection,
          isNotified: true,
        };
      });
    };

    const handleClickOutside = () => {
      if (!isWidget) {
        setActiveSection(-1);
      }
    };

    useUiEventOutsideHandler(rootRef, handleClickOutside);

    const updateLocationSection = useCallback(
      (transportationId: string) => {
        setLocationSection(previousSection => {
          const dropdownData = previousSection.dropdownData as LocationData;
          const selectedTransportationId = transportationId;
          const updatedTransportationOptions =
            dropdownData.transportationOptions.map(option => ({
              ...option,
              isSelected: option.id === selectedTransportationId,
            }));

          // filter locations based on transportation method
          const updatedLocations = dropdownData.locations?.map(group => ({
            ...group,
            options: group.options.map(location => ({
              ...location,
              checked: location.filterIds?.includes(
                parseInt(selectedTransportationId),
              )
                ? location.checked
                : false,
              disabled: !location.filterIds?.includes(
                parseInt(selectedTransportationId),
              ),
            })),
          }));

          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            },
            value: generateLocationValue({
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            }),
          };
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleClickSection = useCallback(
      (sectionId: number) => {
        if (activeSection === sectionId) {
          if (isWidget) return;

          setActiveSection(-1);
        } else {
          setActiveSection(sectionId);

          if (!rootRef.current) return;

          if (!isLaptop) return;

          if (isWidget) return;

          const LARGE_SCROLL_OFFSET = 144;
          const SMALL_SCROLL_OFFSET = 24;
          const elementPosition = rootRef.current.getBoundingClientRect().top;

          if (
            (document.body.getBoundingClientRect().top -
              rootRef.current.getBoundingClientRect().top) *
              -1 <=
            LARGE_SCROLL_OFFSET
          ) {
            window.scrollTo({
              behavior: 'smooth',
              top: elementPosition + window.scrollY - LARGE_SCROLL_OFFSET,
            });
          } else {
            window.scrollTo({
              behavior: 'smooth',
              top: elementPosition + window.scrollY - SMALL_SCROLL_OFFSET,
            });
          }
        }
      },
      [activeSection, isWidget, isLaptop, setActiveSection],
    );

    const handleClickClose = useCallback(() => {
      setActiveSection(-1);
    }, [setActiveSection]);

    const handleClickNext = useCallback(() => {
      setActiveSection(activeSection + 1);
    }, [activeSection, setActiveSection]);

    const handleClickBack = useCallback(() => {
      setActiveSection(activeSection - 1);
    }, [activeSection, setActiveSection]);

    const handleChangeDuration = useCallback(
      (durationId: string) => {
        const departureDateParameter = getDepartureDateParameter();

        if (durationId === 'custom') {
          setDateSection(previousSection => {
            const durationOptions = (
              (previousSection?.dropdownData as DateData)?.durationOptions ?? []
            ).map(option => ({
              ...option,
              isSelected: option.id === durationId,
            }));

            return {
              ...previousSection,
              dropdownData: {
                ...(previousSection.dropdownData as DateData),
                availableDates: [],
                durationOptions,
                maxDate: new Date(
                  brandSearchSettings.maxDepartureDate as string,
                ),
                minDate: new Date(
                  brandSearchSettings.minDepartureDate as string,
                ),
                rangeSelect: true,
                rangeSize: undefined,
                selectedDate: undefined,
                selectedMaxDate: undefined,
              },
              value: generateDateSectionValueString({
                duration: durationId,
                startDate: undefined,
                t,
              }),
            };
          });

          return;
        }

        if (!dataAvailableDates) return;

        const filteredDates = getFilteredAvailableDates(
          dataAvailableDates,
          durationId as Content.Components['PeriodType'],
        );

        const isDateAvailable =
          departureDateParameter &&
          filteredDates.some(date =>
            validateSameDate(date, departureDateParameter),
          );

        let selectedDate = departureDateParameter;
        let noValidDates = false;

        if (!isDateAvailable) {
          selectedDate = filteredDates[0];

          if (!selectedDate) {
            noValidDates = true;
          }
        }

        const formattedDate = dateFormatter(selectedDate).format('D. MMM YYYY');

        setDateSection(previousSection => {
          const durationOptions = (
            (previousSection?.dropdownData as DateData)?.durationOptions ?? []
          ).map(option => ({
            ...option,
            isSelected: option.id === durationId,
          }));

          const selectedOption = durationOptions.find(opt => opt.isSelected);
          const baseDropdownData = {
            ...(previousSection?.dropdownData as DateData),
            durationOptions,
            rangeSelect: false,
            rangeSize: selectedOption?.rangeSize,
          };

          return {
            ...previousSection,
            dropdownData: noValidDates
              ? {
                  ...baseDropdownData,
                  availableDates: [],
                  selectedDate: undefined,
                }
              : {
                  ...baseDropdownData,
                  availableDates: filteredDates,
                  selectedDate,
                },
            isNotified: false,
            value: noValidDates
              ? ''
              : generateDateSectionValueString({
                  duration: durationId,
                  startDate: formattedDate,
                  t,
                }),
          };
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [brandSearchSettings, dataAvailableDates],
    );

    const handleChangeTransportation = useCallback(
      (transportationId: string) => {
        updateLocationSection(transportationId);
      },
      [updateLocationSection],
    );

    const handleChangeLocations = useCallback(
      ({
        destinationRegions,
        destinations,
        hotels,
        locations,
      }: LocationChangeParameters) => {
        setLocationSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              destinationRegionIds: destinationRegions,
              destinations: destinations,
              hotelIds: hotels,
              locations,
            },
            isNotified: false,
            value: generateLocationValue({
              locations,
              transportationOptions: (
                previousSection.dropdownData as LocationData
              ).transportationOptions,
            }),
          };
        });
      },
      [generateLocationValue],
    );

    const handleChangePersons = useCallback(
      (roomsState: RoomsState) => {
        setPersonsSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as PersonsData),
              roomsState: roomsState,
            },
            isLoading: false,
            value: generatePersonsSectionValueString({
              roomsState,
              t,
            }),
          };
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleChangeBrand = useCallback(
      (value: Content.Properties.Brand) => {
        setBrand(value);
        setShouldFetchBrandSearchSettings(true);
        setBrandSection(previousSection => {
          return {
            ...previousSection,
            label: value,
          };
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleChangeDate = useCallback(
      (newDates: Date[]) => {
        const isRange = newDates.length === 2;
        const startDate = newDates[0];
        const endDate = isRange ? newDates[1] : undefined;
        const urlDefaultValues = getSearchParameters(searchParams!);
        const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

        const formattedStartDate = startDate
          ? dateFormatter(startDate).format('D. MMM YYYY')
          : undefined;
        const formattedEndDate = endDate
          ? dateFormatter(endDate).format('D. MMM YYYY')
          : undefined;

        setDateSection(previousSection => {
          const previousDropdown = previousSection.dropdownData as DateData;
          const selectedDuration =
            previousDropdown.durationOptions?.find(option => option.isSelected)
              ?.id ||
            defaultValues.durationType ||
            (!isWidget ? periodType : undefined) ||
            brandSearchSettings.selectedPeriodType;
          const valueString = generateDateSectionValueString({
            duration: selectedDuration,
            endDate: formattedEndDate,
            startDate: formattedStartDate,
            t,
          });

          return {
            ...previousSection,
            dropdownData: {
              ...previousDropdown,
              availableDates: isRange ? [] : previousDropdown.availableDates,
              rangeSelect: isRange,
              rangeSize: isRange ? undefined : previousDropdown.rangeSize,
              selectedDate: startDate,
              selectedMaxDate: endDate,
            },
            isLoading: false,
            isNotified: false,
            value: valueString,
          };
        });
      },
      [
        brandSearchSettings.selectedPeriodType,
        dateFormatter,
        isWidget,
        propDefaultValues,
        searchParams,
        periodType,
        t,
      ],
    );

    const updateSearchBarStateFromUrl = () => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const values = {
        adultsNumber:
          (!isWidget && searchContext.adultsNo) || defaultValues.adultsNumber,
        adultsPerRoom:
          (!isWidget && searchContext.adultsNoPerRoom) ||
          defaultValues.adultsPerRoom,
        childrenAges:
          (!isWidget && searchContext.childrenAges) ||
          defaultValues.childrenAges?.join(','),
        childrenAgesPerRoom:
          (!isWidget && searchContext.childrenAgesPerRoom) ||
          defaultValues.childrenAgesPerRoom,
        departureDate:
          (!isWidget && searchContext.departureDate) ||
          defaultValues.departureDate,
        departureId:
          (!isWidget && searchContext.departureIds) ||
          defaultValues.departureId,
        durationRange: defaultValues.durationRange,
      };

      let rooms = [
        {
          childrenAges: [],
          numberOfAdults:
            brandSearchSettings?.selectedPaxDistribution?.adultsNo,
          numberOfChildren: 0,
        },
      ] as Room[];

      const hasAdultsPerRoom = values.adultsPerRoom;
      const automaticRoomAssignment = !hasAdultsPerRoom;

      if (values.adultsNumber) {
        rooms[0].numberOfAdults = parseInt(values.adultsNumber, 10);
      }

      if (values.childrenAges) {
        rooms[0].childrenAges = values.childrenAges
          .split(',')
          .map(age => parseInt(age, 10));
        rooms[0].numberOfChildren = rooms[0].childrenAges.length;
      }

      if (values.departureId) {
        updateLocationSection(values.departureId);
      }

      if (values.departureDate) {
        const departureDate = new Date(values.departureDate);

        if (values.durationRange !== undefined) {
          handleChangeDate([
            departureDate,
            getRangeEndDate(departureDate, values.durationRange),
          ]);
        } else {
          handleChangeDate([departureDate]);
        }
      }

      if (hasAdultsPerRoom) {
        const adultsArray = values.adultsPerRoom
          .split('|')
          .map((number: string) => parseInt(number, 10));

        rooms = adultsArray.map((numberOfAdults: number) => ({
          childrenAges: [],
          numberOfAdults,
          numberOfChildren: 0,
        }));
      }

      if (values.childrenAgesPerRoom) {
        const childrenAgesArray = values.childrenAgesPerRoom
          .split('|')
          .map(room =>
            room
              .split(',')
              .map(age => (isNaN(parseInt(age, 10)) ? null : parseInt(age, 10)))
              .filter(age => age !== null),
          );

        childrenAgesArray.forEach((childrenAges, index) => {
          if (rooms[index]) {
            rooms[index].childrenAges = childrenAges;
            rooms[index].numberOfChildren = childrenAges.length;
          }
        });
      }

      handleChangePersons({ automaticRoomAssignment, rooms });

      if ((urlDefaultValues.hotelIds ?? []).length > 0) {
        setLocationSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              hotelIds: urlDefaultValues.hotelIds,
            },
          };
        });
      }

      if ((urlDefaultValues.destinationRegionIds ?? []).length > 0) {
        setLocationSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              destinationRegionIds: urlDefaultValues.destinationRegionIds,
            },
          };
        });
      }
    };

    const handleClickSearch = () => {
      searchPageEvents$.next({ type: SearchPageEventType.SEARCH });
      const locale = getLocale();

      const constructAndUpdateSearchUrl = () => {
        const adultsNumber = getAdultCount()?.toString() as string;
        const adultsPerRoom = getAdultsPerRoom();
        const childrenAges = getChildrenAges();
        const childrenAgesPerRoom = getChildrenAgesPerRoom();
        const departureDate = getDepartureDate() as string;
        const departureIds = getTransportationMethod() as string;
        const destinationIds = getCheckedLocations();
        const destinationRegionIds = getDestinationRegionIds();
        const durationRange = getDurationRange()?.toString() as string;
        const durationType = getDuration() as string;
        const hotelIds = getHotelIds();

        const parameters = new URLSearchParams({
          batchSize: brandSearchSettings.batchSize.toString(),
          departureDate,
          departureIds,
          destinationIds,
          destinationRegionIds,
          durationType,
          hotelIds,
          locale: getCulture(locale),
          sortBy: brandSearchSettings.selectedSortBy ?? 'websitePriority',
        });

        if (getAutomaticRoomAssignment()) {
          parameters.append('childrenAges', childrenAges);
          parameters.append('adultsNumber', adultsNumber);
        } else {
          parameters.append('adultsPerRoom', adultsPerRoom);
          parameters.append('childrenAgesPerRoom', childrenAgesPerRoom);
        }

        if (getDuration() === 'custom') {
          parameters.append('durationRange', durationRange);
        }

        const parametersObject: Record<string, any> = {};

        parameters.forEach((value, key) => {
          parametersObject[key] = value;
        });

        setTriggeredBy('searchButton');

        updateCookies({
          adultsNumber,
          adultsPerRoom: !getAutomaticRoomAssignment() ? adultsPerRoom : '',
          childrenAges,
          childrenAgesPerRoom: !getAutomaticRoomAssignment()
            ? childrenAgesPerRoom
            : '',
          departureDate: extractDepartureDateAsISO(dateSection),
          departureIds,
          periodType: durationType,
        });

        addRecentSearch({
          locationSection: {
            value: recentSearchLocationText,
          },
          parameters,
        });

        // if searching for a single hotel, navigate directly to that PDP
        if (
          !hotelIds.includes(',') &&
          destinationRegionIds.length === 0 &&
          destinationIds.length === 0 &&
          dataSearchSingle?.redirect
        ) {
          router.push(dataSearchSingle?.redirect);

          return;
        }

        const hasBrandChanged =
          initialBrand.current === brand ? shouldNavigate : true;

        initialBrand.current = brand;

        updateUrlWithParameters({
          anchorLinkId: configurationFlowAnchorLinkId,
          newParameters: parametersObject,
          overwrite: true,
          redirectUrl: brandSearchSettings.searchPageUrl,
          router,
          shouldNavigate: hasBrandChanged,
          shouldRefresh,
        });
      };

      invalidatePersonsSection();

      if (
        getDuration() === undefined ||
        getTransportationMethod() === undefined ||
        getDepartureDate() === undefined ||
        !isPersonsSectionValid(personsSection)
      ) {
        return;
      }

      const departureDate = dateFormatter(getDepartureDate()).locale('en');
      const countries = [
        ...new Set(
          getCheckedLocations()
            ?.split(',')
            .map(
              destination =>
                dataDestinationHierarchy?.destinations?.find(available =>
                  available?.options.find(item => item.id === destination),
                )?.groupName,
            ),
        ),
      ];
      const destinations = [
        ...new Set(
          getCheckedLocations()
            ?.split(',')
            .map(
              destination =>
                dataDestinationHierarchy?.destinations
                  ?.filter(available =>
                    available?.options.find(item => item.id === destination),
                  )
                  .map(
                    available =>
                      available?.options.find(item => item.id === destination)
                        ?.label,
                  )?.[0],
            ),
        ),
      ];

      trackSubmitSearchEvent({
        search_details: {
          adults: Number(getAdultCount()),
          children: Number(
            getChildrenAges()?.split(',')[0] !== ''
              ? getChildrenAges()?.split(',').length
              : 0,
          ),
          children_ages: String(getChildrenAges()?.split(',').join(', ')),
          country: String(countries?.join(', ').toLowerCase()),
          departure_date: String(departureDate.format('YYYY-MM-DD')),
          departure_day: String(departureDate.date()),
          departure_month: String(departureDate.format('MMM').toLowerCase()),
          departure_week: String(departureDate.week()),
          departure_week_day: String(
            departureDate.format('dddd').toLowerCase(),
          ),
          departure_year: String(departureDate.year()),
          destination: String(destinations?.join(', ').toLowerCase()),
          duration: getDuration() === 'week' ? 7 : 3,
          duration_type: String(getDuration()),
          travelform: String(
            dataDestinationHierarchy?.departures
              .find(departure => departure.id === getTransportationMethod())
              ?.headline.toLowerCase(),
          ),
        },
      });

      setActiveSection(-1);
      setIsClickedEditSearch(false);
      constructAndUpdateSearchUrl();
    };

    useImperativeHandle(ref, () => ({
      search: handleClickSearch,
    }));

    useEffect(() => {
      if (!showBrandSelectorLedge && !showBrandSelectorSection) return;

      setupAndUpdateDurationOptions();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandSearchSettings]);

    useEffect(() => {
      if (!dataSearchSettings) return;

      setBrandSearchSettings(dataSearchSettings);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSearchSettings]);

    useEffect(() => {
      setupAndUpdateDurationOptions();
      updateSearchBarStateFromUrl();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
      setDepartureIds(getTransportationMethod() as string);
      setDestinationIds(getCheckedLocations());
      setHotelIds(getHotelIds());
      setDestinationRegionIds(getDestinationRegionIds());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSection]);

    useEffect(() => {
      const childrenAges = getChildrenAges();
      const adultsNumber = getAdultCount()?.toString() as string;
      const adultsPerRoom = getAdultsPerRoom();
      const childrenAgesPerRoom = getChildrenAgesPerRoom();

      if (getAutomaticRoomAssignment()) {
        setAutomaticRoomAssignment(true);
        setChildrenAges(childrenAges);
        setAdultsNo(adultsNumber);
        setAdultsNoPerRoom('');
        setChildrenAgesPerRoom('');
      } else {
        setAutomaticRoomAssignment(false);
        setAdultsNo('');
        setChildrenAges('');
        setAdultsNoPerRoom(adultsPerRoom);
        setChildrenAgesPerRoom(childrenAgesPerRoom);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personsSection]);

    useEffect(() => {
      setDepartureDate(getDepartureDate() as string);
      setPeriodType(getDuration() as string);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateSection]);

    // Trigger default search when entering search page with no URL parameters
    useEffect(() => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const hasUrlParams =
        urlDefaultValues?.departureDate ||
        urlDefaultValues?.departureId ||
        urlDefaultValues?.destinationIds ||
        urlDefaultValues?.durationType;

      // Only trigger if autoSearch is enabled and no URL params and search bar is ready
      if (
        autoSearch &&
        !hasUrlParams &&
        !isWidget &&
        getDuration() &&
        isEnabledSearchButton
      ) {
        handleClickSearch();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabledSearchButton, autoSearch]);

    const sections = useMemo(
      () =>
        compact && !isWidget && showBrandSelectorSection
          ? [brandSection, locationSection, personsSection, dateSection]
          : [locationSection, personsSection, dateSection],
      [
        compact,
        isWidget,
        showBrandSelectorSection,
        brandSection,
        locationSection,
        personsSection,
        dateSection,
      ],
    );

    return (
      <div
        className={classNames(styles.component, className, {
          [styles.isOpen]: !isWidget && activeSection !== -1,
          [styles.isSearchWidget]: isWidget,
        })}
      >
        <button
          aria-label={t('items.common.words.close')}
          className={styles.overlay}
          onClick={() => {
            setActiveSection(-1);
          }}
          type="button"
        />

        <div ref={rootRef} className={styles.content}>
          <SearchBarWrapper
            activeSection={activeSection}
            buttonLabel={t('items.common.words.search')}
            buttonMode={buttonMode}
            canSubmit={isEnabledSearchButton}
            compact={compact}
            customRangeStyle={getDuration() !== 'custom'}
            isLoadingDate={isFetchingAvailableDates}
            isLoadingLocations={isFetchingDestinationHierarchy}
            isWidget={isWidget}
            onChangeBrand={handleChangeBrand}
            onChangeDate={handleChangeDate}
            onChangeDuration={handleChangeDuration}
            onChangeLocations={handleChangeLocations}
            onChangePersons={handleChangePersons}
            onChangeTransportation={handleChangeTransportation}
            onClickBack={handleClickBack}
            onClickClose={handleClickClose}
            onClickNext={handleClickNext}
            onClickSearch={handleClickSearch}
            onClickSection={handleClickSection}
            searchSettings={brandSearchSettings}
            sections={sections}
          />
        </div>
      </div>
    );
  },
);

SearchBar.displayName = 'SearchBar';
